/**
 * Build a hierarchical diff tree and manage selection for selective sync.
 */

export function collectDiffEntries(nodeDiff) {
    const entries = [];
    const byUuid = {};

    for (const record of nodeDiff.add || []) {
        const entry = {
            uuid: record.uuid,
            type: record.type,
            parentUuid: record.parentUuid || null,
            changeType: 'add',
            displayName: record.displayName || record.data?.name || record.uuid,
        };
        entries.push(entry);
        byUuid[entry.uuid] = entry;
    }
    for (const record of nodeDiff.update || []) {
        const entry = {
            uuid: record.uuid,
            type: record.type,
            parentUuid: record.parentUuid || null,
            changeType: 'update',
            displayName: record.displayName || record.data?.name || record.uuid,
        };
        entries.push(entry);
        byUuid[entry.uuid] = entry;
    }
    for (const detail of nodeDiff.removeDetails || []) {
        const entry = {
            uuid: detail.uuid,
            type: detail.type,
            parentUuid: detail.parentUuid || null,
            changeType: 'remove',
            displayName: detail.displayName || detail.uuid,
        };
        entries.push(entry);
        byUuid[entry.uuid] = entry;
    }
    // Fallback if backend did not send removeDetails
    if (!nodeDiff.removeDetails?.length) {
        for (const uuid of nodeDiff.remove || []) {
            if (!byUuid[uuid]) {
                const entry = {
                    uuid,
                    type: 'item',
                    parentUuid: null,
                    changeType: 'remove',
                    displayName: uuid,
                };
                entries.push(entry);
                byUuid[uuid] = entry;
            }
        }
    }

    return { entries, byUuid };
}

export function buildChildrenMap(entries) {
    const childrenByParent = {};
    for (const entry of entries) {
        if (entry.parentUuid) {
            if (!childrenByParent[entry.parentUuid]) {
                childrenByParent[entry.parentUuid] = [];
            }
            childrenByParent[entry.parentUuid].push(entry.uuid);
        }
    }
    return childrenByParent;
}

export function getRootUuids(entries, byUuid) {
    const childUuids = new Set(entries.map((e) => e.uuid));
    const roots = [];
    for (const entry of entries) {
        const parent = entry.parentUuid;
        if (!parent || !byUuid[parent]) {
            roots.push(entry.uuid);
        }
    }
    // Deduplicate while preserving order
    return [...new Set(roots)];
}

export function collectDescendantUuids(uuid, childrenByParent) {
    const result = [];
    const queue = [uuid];
    const visited = new Set();
    while (queue.length) {
        const current = queue.shift();
        if (visited.has(current)) continue;
        visited.add(current);
        for (const child of childrenByParent[current] || []) {
            result.push(child);
            queue.push(child);
        }
    }
    return result;
}

export function toggleSelection(uuid, selectedSet, childrenByParent, checked) {
    const next = new Set(selectedSet);
    const toToggle = [uuid, ...collectDescendantUuids(uuid, childrenByParent)];
    for (const id of toToggle) {
        if (checked) next.add(id);
        else next.delete(id);
    }
    return next;
}

export function selectAllUuids(entries) {
    return new Set(entries.map((e) => e.uuid));
}

export function countSelectionByType(selectedSet, byUuid) {
    let folders = 0;
    let items = 0;
    for (const uuid of selectedSet) {
        const entry = byUuid[uuid];
        if (!entry) continue;
        if (entry.type === 'folder') folders += 1;
        else items += 1;
    }
    return { folders, items, total: selectedSet.size };
}

export function changeTypeLabel(changeType) {
    switch (changeType) {
        case 'add': return 'New';
        case 'update': return 'Update';
        case 'remove': return 'Remove';
        default: return changeType;
    }
}

export function changeTypeIcon(type) {
    return type === 'folder' ? '📁' : '📄';
}
